import React, {useContext, useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import {FormattedMessage} from "react-intl";

import SectionHeader from "components/dashboard/SectionHeader";
import TopTableHeaderContainer from "../TopTablesComponents/TopTableHeaderContainer";
import TopTableHeaderActionContainer from "../TopTablesComponents/TopTableHeaderActionContainer";
import ExportButton from "../TopTablesComponents/ExportButton";
import ShowMoreButton from "../TopTablesComponents/ShowMoreButton";
import TopRetailersTable from "./components/TopRetailersTable";

import {greyishBlue100} from "assets/jss/main";

import {getRetailersRanking} from "api/dashboard/retailerRanking";
import {exportRetailerRankingCsv} from "api/dashboard/csvExport";

import useScrollTrigger from "hooks/useScrollTrigger";

import {DateRangePickerContext} from "contexts/DateRangePickerContext";

function TopRetailers({ currency, organizationName }) {
    const { startDate, endDate, dateChangedTrigger } = useContext(DateRangePickerContext);

    const [ranking, setRanking] = useState(null);
    const [rankingLoading, setRankingLoading] = useState(false);
    const [showAll, setShowAll] = useState(false);

    const [sectionRef, triggered] = useScrollTrigger(dateChangedTrigger);

    useEffect(() => {
        if (!triggered || !startDate || !endDate) {
            return;
        }

        const start = startDate.format("Y-M-D");
        const end = endDate.format("Y-M-D");

        // a slower response for a previous date range must not overwrite the current one
        let cancelled = false;

        setRanking(null);
        setShowAll(false);

        setRankingLoading(true);
        getRetailersRanking(start, end)
            .then(data => !cancelled && setRanking(data))
            .catch(() => !cancelled && setRanking(null))
            .finally(() => !cancelled && setRankingLoading(false));

        return () => {
            cancelled = true;
        };
    }, [triggered]);

    const totalItems = ranking?.ranking?.length ?? 0;
    const displayData = ranking
        ? { ...ranking, ranking: showAll ? ranking.ranking ?? [] : (ranking.ranking ?? []).slice(0, 10) }
        : null;

    return (
        <div ref={sectionRef}>
            <TopTableHeaderContainer>
                <SectionHeader
                    title={<FormattedMessage id="dashboard.retailersRanking.title" />}
                    subtitle={<FormattedMessage id="dashboard.retailersRanking.subtitle" />}
                />
                <TopTableHeaderActionContainer>
                    <ExportButton
                        disabled={!ranking?.ranking?.length}
                        onClick={() => {
                            exportRetailerRankingCsv(
                                ranking?.ranking ?? [],
                                startDate.format("Y-M-D"),
                                endDate.format("Y-M-D"),
                                organizationName,
                            );
                        }}
                    />
                </TopTableHeaderActionContainer>
            </TopTableHeaderContainer>

            <div
                style={{
                    backgroundColor: "#fff",
                    borderRadius: "0px 0px 12px 12px",
                    border: `1px solid ${greyishBlue100}`,
                    borderTop: 'none',
                    margin: 0
                }}
            >
                <TopRetailersTable data={displayData} loading={rankingLoading} currency={currency} />

                {!showAll && totalItems > 10 && (
                    <ShowMoreButton onClick={() => setShowAll(true)} labelId="dashboard.retailersRanking.showMore" />
                )}
            </div>
        </div>
    );
}

TopRetailers.propTypes = {
    currency: PropTypes.string,
    organizationName: PropTypes.string,
};

export default TopRetailers;
